import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import { ExpandCircleDown } from '@mui/icons-material';
import { AssortmentItemImportResultDTO } from '@retail/my-assortment/types';
import { useMemo } from 'react';
import { useAppTFunction } from '@retail/app/i18n';
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import { dataGridWithCellBordersClass } from '@shared/styles';

interface Props {
  invalidItemsState: AssortmentItemImportResultDTO[];
  totalItemsImported: number;
  failedValidation: boolean;
  countryCode: 'NO' | 'SE';
}

export const ImportStateValidation = ({
  invalidItemsState,
  totalItemsImported,
  failedValidation,
  countryCode,
}: Props) => {
  const t = useAppTFunction();

  const errorColumns: GridColDef<AssortmentItemImportResultDTO>[] = [
    {
      field: 'index',
      flex: 1,
      headerName: t('campaign.import.error.table.index'),
    },
    {
      field: 'mgItemNumber',
      flex: 1,
      headerName: t('myAssortment.productCard.mgItemNr'),
      renderCell: ({ row }) => row.input.mgItemNumber ?? '-',
    },
    {
      field: 'gtin',
      flex: 1,
      headerName: t('myAssortment.packages.gtin'),
      renderCell: ({ row }) => row.input.gtinCode ?? '-',
    },
    {
      field: 'nobb/finfo',
      flex: 1,
      headerName:
        countryCode === 'NO'
          ? t('myAssortment.columns.nobbNumber')
          : t('myAssortment.columns.finfoNumber'),
      renderCell: ({ row }) =>
        countryCode === 'NO' ? row.input.nobbNumber : row.input.finfoNumber ?? '-',
    },
  ];

  const nrOfValidRows = useMemo(
    () => totalItemsImported - invalidItemsState.length,
    [invalidItemsState.length, totalItemsImported]
  );

  const invalidItemNrItems = invalidItemsState?.filter((item) =>
    item.errorCode.includes('MG_ITEM_NOT_FOUND')
  );

  const invalidSupplierIdItems = invalidItemsState?.filter((item) =>
    item.errorCode.includes('SUPPLIER_NOT_FOUND')
  );

  return failedValidation ? (
    <Alert
      severity="info"
      sx={{ display: 'flex', alignItems: 'center', maxWidth: 500, alignSelf: 'center' }}
    >
      <Stack>
        <Typography variant="h5">{t('app.error.oops')}</Typography>
        <Typography variant="body1">
          {t('myAssortment.uploadAssortment.error.validationError')}
        </Typography>
      </Stack>
    </Alert>
  ) : (
    <Stack gap={1}>
      {!!nrOfValidRows && (
        <Alert severity="success" sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography>
            {nrOfValidRows === 1
              ? t('myAssortment.uploadAssortment.itemRead', {
                  nr: nrOfValidRows,
                })
              : t('myAssortment.uploadAssortment.itemsRead', {
                  nr: nrOfValidRows,
                })}
          </Typography>
        </Alert>
      )}

      {invalidSupplierIdItems.length > 0 && (
        <Alert severity="warning" sx={{ display: 'flex', alignItems: 'center' }}>
          <Stack pb={1}>
            <Typography>
              {t('myAssortment.uploadAssortment.invalidSupplierForItem', {
                nr: invalidSupplierIdItems.length,
              })}
            </Typography>
            <Typography>{t('myAssortment.uploadAssortment.invalidSupplierForItemDesc')}</Typography>
          </Stack>

          <StyledAccordion>
            <AccordionSummary expandIcon={<ExpandCircleDown />} id="supplierIds">
              {t('myAssortment.uploadAssortment.error.invalidSupplierIds', {
                count: invalidSupplierIdItems.length,
              })}
            </AccordionSummary>
            <AccordionDetails>
              <DataGridPremium<AssortmentItemImportResultDTO>
                columns={[
                  {
                    field: 'index',
                    flex: 1,
                    headerName: t('campaign.import.error.table.index'),
                  },
                  {
                    field: 'supplierNumber',
                    flex: 1,
                    headerName: t('myAssortment.columns.supplierNumber'),
                    renderCell: ({ row }) => row.input.supplierId ?? '-',
                  },
                ]}
                rows={invalidSupplierIdItems}
                rowSpanning
                getRowId={(row) =>
                  `${row.input.mgItemNumber}-${row.input.supplierId}--${row.index}`
                }
                hideFooter
                className={dataGridWithCellBordersClass}
              />
            </AccordionDetails>
          </StyledAccordion>
        </Alert>
      )}
      {invalidItemNrItems.length > 0 && (
        <Alert severity="error" sx={{ display: 'flex', alignItems: 'center' }}>
          <Stack pb={1}>
            <Typography>
              {t('myAssortment.uploadAssortment.error.invalidItemNumbersInfo', {
                count: invalidItemNrItems.length,
                countryId: countryCode === 'NO' ? 'NOBB' : 'finfo',
              })}
            </Typography>
            <Typography>
              {t('myAssortment.uploadAssortment.error.columns', {
                countryId: countryCode === 'NO' ? 'NOBB' : 'finfo',
              })}
            </Typography>
          </Stack>

          <StyledAccordion>
            <AccordionSummary expandIcon={<ExpandCircleDown />} id="itemNumber">
              {t('myAssortment.uploadAssortment.error.invalidItemNumbers', {
                count: invalidItemNrItems.length,
                countryId: countryCode === 'NO' ? 'NOBB' : 'finfo',
              })}
            </AccordionSummary>
            <AccordionDetails>
              <DataGridPremium<AssortmentItemImportResultDTO>
                columns={errorColumns}
                rows={invalidItemNrItems}
                rowSpanning
                getRowId={(row) =>
                  `${row.input.mgItemNumber}-${row.input.supplierId}--${row.index}`
                }
                hideFooter
                className={dataGridWithCellBordersClass}
              />
            </AccordionDetails>
          </StyledAccordion>
        </Alert>
      )}
    </Stack>
  );
};

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  backgroundColor: 'inherit',
  border: `1px solid ${theme.vars.palette.error.main}`,
}));
